// src/pages/auth/callback.jsx

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authTokenStore } from '../../stores/authToken.store.ts';
import { userStore } from '../../stores/user.store.ts';
import { http } from '../../api/http.ts';
import { showAlert } from '../../utils/dialog.ts';

const FAIL_MESSAGES = {
  invalid_token: 'Invalid token.',
  account_deactivated: 'This account has been deactivated. Please contact support.',
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    async function process() {
      try {
        // 1. Query string에서 status / reason 확인
        const searchSource = location.search || window.__authCallbackSearch || '';
        const params = new URLSearchParams(searchSource);
        const status = params.get('status');
        const reason = params.get('reason');

        if (status === 'fail') {
          await showAlert(FAIL_MESSAGES[reason] ?? `Sign-in failed: ${reason}`);
          navigate('/sign-in', { replace: true });
          return;
        }

        if (status === 'success') {
          // 2. URL Fragment(#)에서 토큰 파싱
          const hashSource = location.hash || window.__authCallbackFragment || '';
          const rawHash = hashSource.startsWith('#') ? hashSource.substring(1) : hashSource;
          const hashParams = new URLSearchParams(rawHash);
          const accessToken = hashParams.get('access_token');
          const expiresIn = hashParams.get('expires_in');
          // URLSearchParams는 '+' → 공백 치환하므로 원복, URL-safe Base64 문자도 보정
          const dataEncoded = hashParams.get('data')?.replace(/ /g, '+');

          if (!accessToken) {
            await showAlert('Sign-in information is invalid. Please try again.');
            navigate('/sign-in', { replace: true });
            return;
          }

          // 3. access_token 저장 (refresh_token은 Set-Cookie로 브라우저가 자동 처리)
          authTokenStore.getState().setAccessToken(accessToken);

          if (expiresIn) {
            const expiresAt = Date.now() + parseInt(expiresIn, 10) * 1000;
            localStorage.setItem('korehalal_access_token_expires_at', String(expiresAt));
          }

          // 4. user 데이터 저장: hash의 Base64 data 우선, 실패 시 API 조회
          // Flutter 참조: base64Url.decode(base64Url.normalize(dataEncoded))
          let userStored = false;
          if (dataEncoded) {
            try {
              // URL-safe Base64 (-, _) → standard Base64 (+, /) 변환 후 패딩 추가
              const b64 = dataEncoded.replace(/-/g, '+').replace(/_/g, '/');
              const padded = b64 + '=='.slice(0, (4 - b64.length % 4) % 4);
              const userData = JSON.parse(atob(padded));
              const userObj = userData?.data ?? userData;
              if (userObj?.uuid) {
                userStore.getState().setUser(userObj);
                userStored = true;
              }
            } catch {
              // Base64 디코딩 실패 → API fallback
            }
          }

          if (!userStored) {
            try {
              const res = await http.get('/web/user/v1/profile');
              const data = res.data?.data ?? res.data;
              if (data?.uuid) {
                userStore.getState().setUser(data);
              }
            } catch {
              // 프로필 조회 실패해도 토큰은 유지 — 다음 새로고침 시 복구
            }
          }

          // 5. GA4 신규 가입 계측.
          // 소셜은 가입과 로그인이 같은 경로라 프런트가 스스로 구분할 수 없고,
          // 서버가 내려주는 is_new 로만 갈린다 — 재로그인에도 쏘면 GA4 의
          // 「가입」이 사실상 로그인 수가 된다. method 는 user 의 login_type
          // 에서 가져온다(콜백 URL 에는 provider 가 없다).
          if (params.get('is_new') === '1') {
            const method = (userStore.getState().user?.login_type || 'social').toLowerCase();
            window.gtag?.('event', 'sign_up', { method });
          }

          // 6. 홈으로 이동
          navigate('/home', { replace: true });
          return;
        }

        // status 없음 또는 알 수 없는 값
        await showAlert('An error occurred during sign-in.');
        navigate('/sign-in', { replace: true });
      } catch {
        await showAlert('An error occurred during sign-in.');
        navigate('/sign-in', { replace: true });
      }
    }

    process();
  }, [navigate, location]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-bg-primary">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
        <p className="text-body-lg text-text-primary">Signing in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
